import { Info } from "lucide-react";
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { ScaledThumbnail } from "./ScaledThumbnail";

interface NinetyDayInteractiveChartCardThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

// Balance line as a percentage of the plot height, sampled across the 90 days
const BALANCE_POINTS = [58, 62, 60, 65, 68, 66, 71, 74, 72, 78, 82, 86];

export default function NinetyDayInteractiveChartCardThumbnail({
  isHovered = false,
  alwaysVisible = false,
}: NinetyDayInteractiveChartCardThumbnailProps) {
  return (
    <ScaledThumbnail
      isHovered={isHovered}
      alwaysVisible={alwaysVisible}
      contentWidth={440}
    >
      <Card className="w-[440px]">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-1">
            <span className="text-lg font-semibold text-neutral-900">
              90-Day Cash Flow Forecast
            </span>
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </div>
        <CardContent className="pt-4">
          <div className="space-y-4">
            {/* Legend */}
            <div className="flex items-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded"
                  style={{
                    backgroundColor: "#dcfce7",
                    border: "1px solid var(--mc-success-icon)",
                  }}
                />
                <span className="text-xs text-neutral-600">
                  Healthy (&gt;£100K)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded"
                  style={{
                    backgroundColor: "#fef3c7",
                    border: "1px solid var(--mc-warning-icon)",
                  }}
                />
                <span className="text-xs text-neutral-600">
                  Caution (£50K–£100K)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded"
                  style={{
                    backgroundColor: "#fee2e2",
                    border: "1px solid var(--mc-error-icon)",
                  }}
                />
                <span className="text-xs text-neutral-600">
                  Critical (&lt;£50K)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0 border-t-2 border-dashed border-red-500" />
                <span className="text-xs text-neutral-600">
                  Threshold (£50K)
                </span>
              </div>
            </div>

            {/* Chart — static stand-in for the interactive recharts timeline */}
            <div className="flex gap-2">
              <div className="flex flex-col justify-between text-xs text-neutral-500">
                <span>£350K</span>
                <span>£200K</span>
                <span>£50K</span>
              </div>
              <div className="flex-1">
                <div className="relative h-[180px] overflow-hidden rounded-sm">
                  {/* Colour-coded zones */}
                  <div
                    className="absolute inset-x-0 top-0 h-1/2"
                    style={{ backgroundColor: "#dcfce7" }}
                  />
                  <div
                    className="absolute inset-x-0 top-1/2 h-1/4"
                    style={{ backgroundColor: "#fef3c7" }}
                  />
                  <div
                    className="absolute inset-x-0 bottom-0 h-1/4"
                    style={{ backgroundColor: "#fee2e2" }}
                  />
                  {/* Threshold line */}
                  <div className="absolute inset-x-0 bottom-1/4 border-t-2 border-dashed border-red-500" />
                  {/* Projected balance */}
                  <div className="absolute inset-0 flex items-end gap-1 px-1">
                    {BALANCE_POINTS.map((height, index) => (
                      <div
                        key={index}
                        className="flex-1 flex flex-col justify-end"
                      >
                        <div
                          className="w-full rounded-t"
                          style={{
                            height: `${height}%`,
                            backgroundColor: "var(--accent-sky-500)",
                            opacity: 0.85,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between text-xs text-neutral-500 mt-1">
                  <span>Today</span>
                  <span>Day 45</span>
                  <span>Day 90</span>
                </div>
              </div>
            </div>

            {/* Selected day summary */}
            <div className="rounded-lg border border-border p-3">
              <h4 className="font-semibold text-sm mb-2">Day 90</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Projected Balance</p>
                  <p className="font-bold text-lg">£342,100</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Incoming</p>
                  <p className="font-bold text-lg text-green-600">£96,400</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Outgoing</p>
                  <p className="font-bold text-lg text-red-600">£41,750</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </ScaledThumbnail>
  );
}
